import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Palette, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLatestBeautyScan } from "./useLatestBeautyScan";
import { ScanRequiredEmpty, ShopRecommendedButton } from "./beautyUi";
import StylePreviewButton from "./StylePreviewButton";

const OCCASIONS = ["Everyday", "Office", "College", "Party", "Wedding", "Reception", "Festival", "Date Night", "Traditional", "Photoshoot"];
const VIBES = ["Auto — pick the best", "Natural · No-makeup makeup", "Soft glam", "Bold & smoky", "Editorial · runway", "Romantic dewy", "Matte sculpted", "Vintage Hollywood"];
const MODES = [
  { id: "beginner", label: "Beginner" },
  { id: "professional", label: "Professional" },
];

export default function BeautyMakeup() {
  const { scan, loading } = useLatestBeautyScan();
  const [occasion, setOccasion] = useState("");
  const [vibe, setVibe] = useState("");
  const [mode, setMode] = useState("beginner");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!occasion) { toast.error("Pick an occasion first."); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/beauty/makeup/look", {
        occasion, vibe: vibe && !vibe.startsWith("Auto") ? vibe : undefined, mode,
        scan_id: scan?.id,
      });
      setResult(data.result);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't generate that look.";
      toast.error(String(msg));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="max-w-5xl mx-auto p-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!scan) return <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10"><ScanRequiredEmpty /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-makeup-page">
      <p className="eyebrow flex items-center gap-2"><Palette size={12} /> Stylebay · Makeup Studio</p>
      <h1 className="editorial-h1 mt-3">Makeup, <em>matched to your scan.</em></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Palettes and step-by-step choreography — tuned to your face shape, skin tone and undertone from the AI Scan.
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr,1.4fr] gap-10">
        <section className="border border-border p-6 lg:p-8 space-y-6 self-start" data-testid="beauty-makeup-form">
          <div>
            <p className="eyebrow mb-3">Occasion</p>
            <Select value={occasion} onValueChange={setOccasion}>
              <SelectTrigger data-testid="beauty-makeup-occasion"><SelectValue placeholder="Where are you headed?" /></SelectTrigger>
              <SelectContent>{OCCASIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="eyebrow mb-3">Vibe (optional)</p>
            <Select value={vibe} onValueChange={setVibe}>
              <SelectTrigger data-testid="beauty-makeup-vibe"><SelectValue placeholder="Pick a vibe" /></SelectTrigger>
              <SelectContent>{VIBES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="eyebrow mb-3">Tutorial mode</p>
            <div className="grid grid-cols-2 gap-2">
              {MODES.map(m => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`beauty-makeup-mode-${m.id}`}
                  onClick={() => setMode(m.id)}
                  className={`h-10 text-[11px] uppercase tracking-[0.2em] border ${mode === m.id ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"}`}
                >{m.label}</button>
              ))}
            </div>
          </div>
          <Button disabled={busy || !occasion} onClick={run} className="w-full" data-testid="beauty-makeup-generate">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mixing your palette…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate look</>}
          </Button>
        </section>

        <section data-testid="beauty-makeup-result">
          {!result && !busy && (
            <div className="border border-dashed border-border p-10 text-center">
              <Palette size={26} strokeWidth={1.3} className="mx-auto opacity-60" />
              <p className="font-serif text-xl mt-4">Your look appears here.</p>
              <p className="text-xs text-muted-foreground mt-2 max-w-sm mx-auto">
                Pick an occasion, and we&apos;ll build a step-by-step palette that suits the scan on file.
              </p>
            </div>
          )}
          {result && <MakeupOutput data={result} occasion={occasion} vibe={vibe} />}
        </section>
      </div>
    </div>
  );
}

function MakeupOutput({ data, occasion, vibe }) {
  const { summary, application_order = [], palette = {}, shop_categories = [], do: dos = [], dont: donts = [] } = data;
  // Build a compact "notes" string that captures the palette / vibe so the
  // Nano Banana preview matches the recommended look, not just the occasion.
  const previewName = vibe && !vibe.startsWith("Auto") ? `${occasion} — ${vibe}` : occasion;
  const paletteHint = [
    palette.foundation_undertone && `undertone: ${palette.foundation_undertone}`,
    palette.lip_colors?.[0]?.name && `lip: ${palette.lip_colors[0].name}`,
    palette.eye_colors?.[0]?.name && `eye: ${palette.eye_colors[0].name}`,
    palette.blush_contour?.[0]?.name && `blush: ${palette.blush_contour[0].name}`,
  ].filter(Boolean).join(", ");

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      {summary && <p className="font-serif text-2xl italic leading-snug">&ldquo;{summary}&rdquo;</p>}

      {/* Preview-on-me CTA sits right below the summary so it's the first
          call to action on the look. */}
      <div className="flex flex-wrap items-center gap-3 border border-border p-4">
        <StylePreviewButton
          kind="makeup"
          styleName={previewName}
          styleNotes={paletteHint}
          testId="beauty-makeup-preview-btn"
          label="Preview this look on me"
        />
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Uses your identity photo · powered by Nano Banana AI
        </p>
      </div>

      {palette.foundation_undertone && (
        <p className="text-xs"><span className="eyebrow">Undertone · </span>{palette.foundation_undertone}</p>
      )}

      <ColourGrid title="Base"       colors={palette.base_colors} />
      <ColourGrid title="Eye"        colors={palette.eye_colors} />
      <ColourGrid title="Lips"       colors={palette.lip_colors} />
      <ColourGrid title="Blush / Contour" colors={palette.blush_contour} />

      {application_order.length > 0 && (
        <div>
          <p className="eyebrow mb-3">Application order</p>
          <ol className="space-y-2">
            {application_order.map((s, i) => (
              <li key={i} className="border-l-2 border-foreground pl-4 py-2">
                <p className="text-sm"><span className="eyebrow mr-2">{String(s.step || i + 1).padStart(2, "0")}</span><strong>{s.product_type || s.product_category}</strong>{s.color_name ? ` · ${s.color_name}` : ""}</p>
                {s.technique && <p className="text-xs text-muted-foreground mt-1">{s.technique}</p>}
                {s.tip && <p className="text-xs text-accent mt-1">Tip · {s.tip}</p>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {(dos.length > 0 || donts.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-6">
          <TipList label="Do" items={dos} tone="positive" />
          <TipList label="Don't" items={donts} tone="negative" />
        </div>
      )}

      {shop_categories.length > 0 && (
        <div className="border border-border p-5">
          <p className="eyebrow mb-3">Shop for this look</p>
          <div className="flex flex-wrap gap-2">
            {shop_categories.map((c, i) => (
              <ShopRecommendedButton
                key={i}
                category={c.category_slug || c.label?.toLowerCase().replace(/\s+/g, "_") || "foundation"}
                label={c.label || "Shop products"}
                testId={`beauty-makeup-shop-${i}`}
              />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function ColourGrid({ title, colors }) {
  if (!colors || !colors.length) return null;
  return (
    <div>
      <p className="eyebrow mb-3">{title}</p>
      <ul className="grid sm:grid-cols-2 gap-3">
        {colors.map((c, i) => (
          <li key={i} className="flex items-center gap-3 border border-border p-3">
            <span className="h-10 w-10 shrink-0 border border-border" style={{ background: c.hex || "#ccc" }} />
            <div className="min-w-0">
              <p className="font-serif text-base truncate">{c.name}</p>
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{c.hex}</p>
              {c.purpose && <p className="text-xs text-muted-foreground mt-1 leading-snug">{c.purpose}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TipList({ label, items, tone }) {
  if (!items?.length) return null;
  const colour = tone === "positive" ? "text-foreground" : "text-muted-foreground";
  return (
    <div>
      <p className="eyebrow mb-2">{label}</p>
      <ul className={`text-sm space-y-1 ${colour} list-disc list-inside`}>{items.map((s, i) => <li key={i}>{s}</li>)}</ul>
    </div>
  );
}
